import Decimal from 'decimal.js'

export function formatCurrency(value: unknown) {
  if (typeof value !== 'number') {
    return '0.00' // 或者抛出错误，根据你的需求
  }

  // 1. 使用 toFixed 处理精度问题并保留两位小数
  const numString = parseFloat(value.toFixed(2))

  // 2. 将数字转换为字符串
  const numStr = String(numString)

  // 3. 分割整数部分和小数部分
  const [integerPart, decimalPart] = numStr.split('.')

  // 4. 添加千分位分隔符
  let formattedInteger = ''
  for (let i = integerPart.length - 1, count = 0; i >= 0; i--) {
    formattedInteger = integerPart[i] + formattedInteger
    count++
    if (count % 3 === 0 && i !== 0) {
      formattedInteger = ',' + formattedInteger
    }
  }

  // 5. 拼接整数部分和小数部分
  const formattedNumber = decimalPart
    ? formattedInteger + '.' + decimalPart
    : formattedInteger + '.00'

  return formattedNumber
}

export function formatNumberWithCommas(value: unknown) {
  // 确保是数字类型，如果传入的是非数字，返回空字符串
  if (typeof value !== 'number' || isNaN(value)) {
    return ''
  }

  // 使用 toFixed(2) 将值保留两位小数，同时确保不会受浮点误差影响
  // 然后用正则表达式添加千分号
  return parseFloat(value.toFixed(2)).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function formatEquityValue(value: unknown) {
  // 确保传入的是一个数值类型
  const num = Number(value)

  if (isNaN(num)) {
    throw new Error('Invalid number provided')
  }

  // 执行计算，考虑到 JavaScript 的浮点数运算可能导致的小数点误差，
  // 我们可以先将数值乘以100再进行取整，然后再除以100来尽量减少误差。
  const calculatedValue = Math.round(num * 100) / 100

  // 使用 Intl.NumberFormat 来格式化数字，包括保留两位小数和添加千分号
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return formatter.format(calculatedValue)
}

export const formatNumberWithDefault = (
  value: unknown,
  decimalPlaces = 2,
  defaultValue = '--',
  unit = '',
) => {
  if (value === null || isNaN(Number(value))) {
    return defaultValue
  }
  // 使用 Intl.NumberFormat 来格式化数字，包括保留两位小数和添加千分号
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })
  return formatter.format(Number(value)) + ` ${unit}`
}

export const initNumberSymbol = (value: number | string): string => {
  if (typeof value === 'string') {
    value = Number(value)
  }
  if (value > 0) {
    return '+'
  }
  if (value < 0) {
    return ''
  }
  return ''
}

export const initNumberColor = (value: number | string): string => {
  if (typeof value === 'string') {
    value = Number(value)
  }
  if (value > 0) {
    return 'text-success-500!'
  }
  if (value < 0) {
    return 'text-danger-500!'
  }
  return ''
}

export const getNewMargin = (margin: number, leverage: number, needLeverage: boolean) => {
  const marginInUsd = new Decimal(margin)
  return needLeverage ? marginInUsd.mul(100).div(leverage).toNumber() : margin
}

export const getPriceInfoFromSocketMessageBySymbolName = (
  socketMessage: string,
  symbolName: string,
  separator = ' ',
) => {
  const parts = socketMessage.split(separator)
  const [_symbolName, ...priceInfo] = parts
  const isMarch = _symbolName === symbolName && priceInfo.length === 7
  return isMarch ? priceInfo : null
}

/**
 * 格式化数值，支持设置精度和千位分隔符
 * @param value - 需要格式化的数值
 * @param decimalPlaces - 小数位数
 * @param useThousandSeparator - 是否使用千位分隔符（默认为 true）
 * @returns 格式化后的字符串
 */
export const formatNumber = (
  value: number,
  decimalPlaces: number,
  useThousandSeparator = true,
): string => {
  const fixedValue = value.toFixed(decimalPlaces) // 按指定小数位数格式化
  return useThousandSeparator
    ? fixedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',') // 添加千位分隔符
    : fixedValue // 直接返回格式化后的值
}

/**
 * 格式化数值并添加单位，支持设置精度、千位分隔符以及单位显示
 * @param value - 需要格式化的数值
 * @param decimalPlaces - 小数位数
 * @param useThousandSeparator - 是否使用千位分隔符（默认为 true）
 * @param showUnit - 是否显示单位（默认为 false）
 * @param unitPosition - 单位的位置，可以是 'before'、'after' 或 'both'，默认为 'none'
 * @param unitBefore - 单位的前置部分，只有在 `unitPosition` 为 'both' 或 'before' 时有效
 * @param unitAfter - 单位的后置部分，只有在 `unitPosition` 为 'both' 或 'after' 时有效
 * @returns 格式化后的字符串
 */
export const formatNumberWithUnit = (
  value: number,
  decimalPlaces: number,
  useThousandSeparator = true,
  unitBefore = '',
  unitAfter = '',
): string => {
  const formattedValue = formatNumber(value, decimalPlaces, useThousandSeparator)
  return `${unitBefore}${formattedValue} ${unitAfter}`
}

export const parseStringToNumber = (str: string, defaultValue: number = 0): number => {
  const num = parseInt(str, 10)
  return isNaN(num) ? defaultValue : num
}

export const ensureNumber = (value: number | string, defaultValue: number = 0): number => {
  return typeof value === 'number' ? value : parseStringToNumber(value, defaultValue)
}

export const getSafeNumber = (
  value: number | string | undefined,
  defaultValue: number = 0,
): number => {
  return value === undefined ? defaultValue : ensureNumber(value, defaultValue)
}

/**
 * 格式化价格：根据 digits 字段决定精度（价格为 0 时显示 '--'）
 * @param value - 需要格式化的价格值
 * @param digits - 小数位数，默认为 2
 * @returns 格式化后的字符串，无效值返回 '--'
 */
export const formatPrice = (value: any, digits?: number): string => {
  // 检查是否有值
  if (value === null || value === undefined || value === '') return '--'

  // 尝试转换为数字
  const num = Number(value)

  // 检查是否是有效数字
  if (isNaN(num)) return '--'

  // 如果是 0，显示为 --（价格不可能为 0）
  if (num === 0) return '--'

  // 根据 digits 设置精度，默认为 2
  const precision = digits !== undefined && digits !== null ? digits : 2
  return num.toFixed(precision)
}

/**
 * 格式化金额：根据 digits 字段决定精度（0 是有效值，显示为 '0.00'）
 * 用于佣金、隔夜利息、盈亏等金额类字段
 * @param value - 需要格式化的金额值
 * @param digits - 小数位数，默认为 2
 * @returns 格式化后的字符串，无效值返回 '--'
 */
export const formatAmount = (value: any, digits: number = 2): string => {
  // 检查是否有值
  if (value === null || value === undefined || value === '') return '--'

  // 尝试转换为数字
  const num = Number(value)

  // 检查是否是有效数字
  if (isNaN(num)) return '--'

  // 0 是有效值，不返回 '--'
  return num.toFixed(digits)
}
